/**
 * Capa de dispositivo — contrato común.
 *
 * Todo lo que el teléfono puede negar (ubicación, cámara, notificaciones,
 * almacenamiento) se accede a través de esta carpeta, nunca llamando a la API del
 * navegador desde un componente (spec §3.3). Hoy cada adaptador tiene implementación
 * web; al envolver con Capacitor se sustituye el archivo y ninguna pantalla cambia.
 *
 * Regla de diseño: **estos adaptadores no lanzan excepciones**. Devuelven un
 * resultado explícito. Un permiso negado o un GPS sin señal es un caso de negocio
 * normal —la marca se registra igual y se señala (§8.3.3)—, no un error de programa.
 */

export type DeviceFailureReason =
  /** El navegador no ofrece la capacidad (o corremos en servidor). */
  | "unsupported"
  /** El usuario negó el permiso explícitamente. */
  | "denied"
  /** La API existe y el permiso está dado, pero falló: sin señal, disco lleno, modo privado. */
  | "unavailable"
  /** La operación tardó más de lo aceptable. */
  | "timeout"
  /** El usuario cerró el diálogo sin elegir nada. */
  | "cancelled";

export type DeviceResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: DeviceFailureReason; message: string };

export const deviceOk = <T,>(value: T): DeviceResult<T> => ({ ok: true, value });

export const deviceFail = <T,>(
  reason: DeviceFailureReason,
  message: string,
): DeviceResult<T> => ({ ok: false, reason, message });

/** `false` en el servidor y en cualquier render previo a la hidratación. */
export const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof navigator !== "undefined";

/**
 * `crypto.randomUUID` no existe en contextos no seguros (http en LAN, que es
 * exactamente donde se prueba una app de campo). El respaldo no necesita ser
 * criptográfico: solo irrepetible, porque es la llave de idempotencia de la cola.
 */
export function createUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const random = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, "0");
  return `${random()}${random()}-${random()}-4${random().slice(1)}-a${random().slice(1)}-${random()}${random()}${random()}`;
}
